import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

interface ServiceProps {
  title: string;
  description: string;
}

const Container = styled.div`
  max-width: 100%; // 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
  font-family: "Manrope", sans-serif;
  box-sizing: border-box;
`;

const Title = styled(motion.h2)`
  font-size: 2rem;
  color: #102a8e;
  text-align: center;
  margin-bottom: 3rem;
  font-weight: bold;
`;

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
  overflow: hidden;
`;

const LeafDecoration = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 34px;
  height: 34px;
  transition: all 0.3s ease;

  svg {
    width: 100%;
    height: 100%;
    transition: all 0.3s ease;
  }

  path {
    transition: fill 0.3s ease;
  }

  ${Card}:hover & {
    g {
      opacity: 1;
    }
    path {
      fill: #5575f7 !important;
    }
  }
`;

const CardTitle = styled.h3`
  font-size: 14px;
  color: #131313;
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const CardText = styled.p`
  color: #131313;
  line-height: 1.5;
  font-size: 16px;
`;

export const Whatweoffer: React.FC = () => {
  const services: ServiceProps[] = [
    {
      title: "Structured Professional Preparation",
      description:
        "Practical, role-specific preparation focused on real recruitment expectations in UK healthcare.",
    },
    {
      title: "Interview & Assessment Readiness",
      description:
        "Prepare for interviews, scenario questions, and professional discussions with targeted resources.",
    },
    {
      title: "Confidence & Progress Tracking",
      description: "Track your preparation journey and build confidence for interviews and shortlisting stages.",
    },
    {
      title: "Appointment Focused Preparation",
      description: "Focus your preparation on what recruiters actually assess when making appointment decisions.",
    },
    {
      title: "Application & Interview Guidance",
      description:
        "Clear guidance on applications CVs, supporting information, and interview readiness for healthcare roles.",
    },
    {
      title: "Job Application Resources",
      description: "Curated links and resources to support healthcare job applications across NHS and private sectors.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    hover: {
      scale: 1.02,
      backgroundColor: "#F5F8FF",
      transition: { duration: 0.3, ease: "easeInOut" },
    },
  };

  return (
    <Container>
      <Title initial="hidden" animate="visible" variants={titleVariants}>
        What Compelling Candidature Helps You Achieve
      </Title>

      <Grid initial="hidden" animate="visible" variants={containerVariants}>
        {services.map((service, index) => (
          <Card
            key={index}
            variants={cardVariants}
            whileHover="hover"
            initial="hidden"
            animate="visible"
            custom={index}
          >
            <LeafDecoration>
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g opacity="0.45">
                  <path
                    d="M26.9594 0C26.9594 0 30.4931 8.645 15.3482 14.4518C7.11882 17.6062 0.798162 25.6912 8.34502 38.1805C8.97002 32.5979 11.65 20.2893 23.1737 17.4805C23.1737 17.4805 12.4481 21.4911 11.5007 39.6454C17.0119 40.4192 29.9374 40.9391 33.775 29.7223C38.8425 14.9024 26.9594 0 26.9594 0Z"
                    fill="#E6EBE3"
                  />
                  <path
                    d="M11.5057 39.5699C23.2056 34.9005 31.7187 23.9618 32.8937 10.9037C30.5018 4.45183 26.9594 0 26.9594 0C26.9594 0 30.4931 8.645 15.3482 14.4518C7.11882 17.6062 0.798162 25.6912 8.34502 38.1805C8.97002 32.5979 11.65 20.2893 23.1737 17.4805C23.1731 17.4806 12.4794 21.4812 11.5057 39.5699Z"
                    fill="#ECEFEA"
                  />
                  <path
                    d="M7.17752 14.7398C7.78159 14.7398 8.27129 14.2501 8.27129 13.646C8.27129 13.0419 7.78159 12.5522 7.17752 12.5522C6.57344 12.5522 6.08374 13.0419 6.08374 13.646C6.08374 14.2501 6.57344 14.7398 7.17752 14.7398Z"
                    fill="#E6EBE3"
                  />
                  <path
                    d="M10.615 13.334C11.0464 13.334 11.3962 12.9842 11.3962 12.5527C11.3962 12.1213 11.0464 11.7715 10.615 11.7715C10.1835 11.7715 9.83374 12.1213 9.83374 12.5527C9.83374 12.9842 10.1835 13.334 10.615 13.334Z"
                    fill="#ECEFEA"
                  />
                  <path
                    d="M9.91189 7.08391C10.9905 7.08391 11.865 6.20948 11.865 5.13082C11.865 4.05216 10.9905 3.17773 9.91189 3.17773C8.83323 3.17773 7.9588 4.05216 7.9588 5.13082C7.9588 6.20948 8.83323 7.08391 9.91189 7.08391Z"
                    fill="#ECEFEA"
                  />
                  <path
                    d="M15.9274 10.209C16.2726 10.209 16.5524 9.92917 16.5524 9.58399C16.5524 9.23881 16.2726 8.95898 15.9274 8.95898C15.5823 8.95898 15.3024 9.23881 15.3024 9.58399C15.3024 9.92917 15.5823 10.209 15.9274 10.209Z"
                    fill="#ECEFEA"
                  />
                  <path
                    d="M21.1625 7.8652C22.1548 7.8652 22.9593 7.06072 22.9593 6.06834C22.9593 5.07596 22.1548 4.27148 21.1625 4.27148C20.1701 4.27148 19.3656 5.07596 19.3656 6.06834C19.3656 7.06072 20.1701 7.8652 21.1625 7.8652Z"
                    fill="#ECEFEA"
                  />
                </g>
              </svg>
            </LeafDecoration>
            <CardTitle>{service.title}</CardTitle>
            <CardText>{service.description}</CardText>
          </Card>
        ))}
      </Grid>
    </Container>
  );
};
